import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { paySchema } from '../../../../shared/validators/schemas/pay.schema';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { VehicleRoadTaxRecordDto } from '../../dtos/vehicle-road-tax-record.dto';
import { VehicleRoadTaxActionEnum } from '../../enums/vehicle-road-tax-action.enum';
import { VehicleRoadTaxStatusEnum } from '../../enums/vehicle-road-tax-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: VehicleRoadTaxRecordDto,
	action: VehicleRoadTaxActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		VehicleRoadTaxStatusEnum.PENDING,
		VehicleRoadTaxStatusEnum.EXPIRED,
		VehicleRoadTaxStatusEnum.ACTIVE,
	];

	switch (action) {
		case VehicleRoadTaxActionEnum.APPROVE:
			// TODO: Approval flow
			break;

		case VehicleRoadTaxActionEnum.REJECT:
			// TODO: Approval flow
			break;

		case VehicleRoadTaxActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				VehicleRoadTaxStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleRoadTaxActionEnum.PENDING:
			validationResult = setStatusSchema(
				VehicleRoadTaxStatusEnum.PENDING,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleRoadTaxActionEnum.EXPIRE:
			validationResult = setStatusSchema(
				VehicleRoadTaxStatusEnum.EXPIRED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleRoadTaxActionEnum.PAY:
			validationResult = paySchema().validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
